<template>
  <component
    :is="tag"
    class="m-button-icon"
    :class="{
      [`m-button-icon--${buttonStyle}`]: !!buttonStyle,
    }"
    :data-qa-locator="qaLocator"
    :aria-label="ariaLabel"
    :type="type"
    v-bind="$attrs"
  >
    <MIcon :size="size" :icon="icon" />
  </component>
</template>

<script>
import { defineComponent } from 'vue'

import { clickableTag } from '../../../lib/type-validators'
import MIcon from '../../typography/Icon/MIcon'
/**
 * Button component for use-cases where only an icon is desired,
 * e.g. like a pencil for editing a value or an arrow to download something
 */
export default defineComponent({
  name: 'MButtonIcon',
  components: { MIcon },
  props: {
    /**
     * Style variation:
     * - no-background removes the background hover.
     */
    buttonStyle: {
      type: String,
      default: null,
      validator: (prop) => ['no-background'].includes(prop),
    },
    /**
     * Accessible button text used by screen readers
     */
    ariaLabel: {
      type: String,
      required: true,
    },
    /** Icon to display - see MIcon for valid values */
    icon: {
      type: String,
      required: true,
    },
    /** Size of icon to display - see MIcon for valid values */
    size: {
      type: String,
      default: undefined,
    },
    /**
     * HTML tag
     */
    tag: {
      type: [Object, String],
      default: 'button',
      validator: clickableTag,
    },
    /** Adds a data-qa-locator, used by system tests to locate elements in the browser */
    qaLocator: {
      type: String,
      default: '',
    },
    /**
     * Button type
     */
    type: {
      type: String,
      default: 'button',
      validator: (prop) => ['button', 'submit'].includes(prop),
    },
  },
})
</script>

<style lang="scss" scoped>
@use '../../../sass/framework/mixins/clickable';
@use '../../../sass/framework/mixins/hover';
@use '../../../sass/framework/mixins/font-size';

.m-button-icon {
  @include clickable.clickable;
  @include font-size.font-size(lg);
  align-items: center;
  appearance: none;
  background: none;
  border: 0 none;
  border-radius: 50%;
  color: var(--skin-clickable-color);
  display: inline-flex;
  flex-grow: 0;
  flex-shrink: 0;
  justify-content: center;
  line-height: 1;
  max-height: min-content;
  max-width: min-content;
  vertical-align: middle;
  width: min-content;

  &--no-background {
    @include hover.hover-transition(color);
    @include hover.hover-styles {
      background: none;
      color: var(--skin-link-color-hover);
    }
  }
}
</style>
